import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Form,
  Input,
  Row,
  Col,
  Table,
  Tag,
  Modal,
  Select,
  Progress,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import QRCode from 'qrcode';

import PageHeader from 'utility/PageHeader';
import PageBody from 'utility/PageBody';
import CampaignActions, {
  combineCampaignReUrl,
  getHotaiTags,
} from './CampaignMaintenanceUtils';

const FilterSchema = {
  name: '',
  url: '',
  tag: '',
  remark: '',
};

const SearchBar = ({ initialValues, onSearch, onCreate, tagList }) => {
  return (
    <Form
      name="campaignSearchForm"
      onFinish={onSearch}
      initialValues={initialValues}>
      <Row gutter={16}>
        <Col span={8} style={{ maxWidth: 300, minWidth: 200 }}>
          <Form.Item label="活動名稱" name="name">
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col span={8} style={{ maxWidth: 300, minWidth: 200 }}>
          <Form.Item label="活動網址" name="url">
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col span={8} style={{ maxWidth: 300, minWidth: 200 }}>
          <Form.Item label="Tag ID" name="tag" help="目前僅支援單一標籤搜尋">
            {/* <Input allowClear /> */}
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={tagList}
            />
          </Form.Item>
        </Col>
        <Col span={8} style={{ maxWidth: 600, minWidth: 300 }}>
          <Form.Item label="備註" name="remark">
            <Input allowClear />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={16} className="button-group">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onCreate}
            className="button button--register">
            新增
          </Button>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            htmlType="submit"
            className="button button--search">
            搜尋
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const DataTable = ({
  dataSource,
  isLoading,
  modalToggle,
  setShadow,
  reFetchDataFunc,
  tagList,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEventUrl, setCurrentEventUrl] = useState('');
  const canvasRef = useRef(null);

  useEffect(() => {
    if (currentEventUrl && canvasRef.current) {
      QRCode.toCanvas(
        canvasRef.current,
        currentEventUrl,
        {
          width: 256,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#ffffff',
          },
        },
        (error) => {
          if (error) console.error('QRCode generation error:', error);
        }
      );
    }
  }, [currentEventUrl]);

  const selectItem = (record) => {
    setCurrentEventUrl(combineCampaignReUrl(record));
    setIsModalOpen(true);
  };
  const columns = [
    {
      title: 'QRCode',
      dataIndex: 'qrcode',
      key: 'qrcode',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<QrcodeOutlined />}
          onClick={() => selectItem(record)}
        />
      ),
    },
    {
      title: '活動名稱',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '活動網址',
      dataIndex: 'url',
      key: 'url',
    },
    {
      title: 'Tag ID',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags) => {
        const filteredTags = tagList.filter((tag) => tags.includes(tag.value));
        return (
          <>
            {filteredTags.map((tag) => (
              <Tag color="default" key={tag.value}>
                {tag.label}
              </Tag>
            ))}
          </>
        );
      },
    },
    {
      title: '備註',
      dataIndex: 'remark',
      key: 'remark',
    },
    {
      title: '操作',
      dataIndex: 'operation',
      key: 'operation',
      render: (_, record) => [
        <Button
          key="edit"
          type="primary"
          icon={<EditOutlined />}
          onClick={() => {
            setShadow(record);
            modalToggle(true);
          }}>
          編輯
        </Button>,
        <Button
          key="delete"
          type="primary"
          danger
          icon={<DeleteOutlined />}
          className="button--delete"
          style={{ marginLeft: '8px' }}
          onClick={() => {
            Modal.confirm({
              title: '刪除入口碼',
              content: `你確定要刪除這個入口碼嗎？`,
              okText: '確定',
              cancelText: '取消',
              onOk() {
                return CampaignActions.deleteCampaign(record.id)
                  .then((result) => {
                    if (result.success) {
                      window.antdNotify(
                        '活動入口碼',
                        '活動刪除成功',
                        'success'
                      );
                      reFetchDataFunc();
                    } else {
                      window.antdNotify('活動入口碼', '活動刪除失敗', 'fail');
                    }
                  })
                  .catch((error) => {
                    console.error('Error deleting campaign:', error);
                    window.antdNotify('活動入口碼', '活動刪除失敗', 'fail');
                  });
              },
            });
          }}>
          刪除
        </Button>,
      ],
    },
  ];

  const handleCopy = () => {
    if (currentEventUrl) {
      navigator.clipboard.writeText(currentEventUrl).then(() => {
        window.antdNotify('活動入口碼', '已複製活動網址到剪貼簿', 'success');
      });
    }
  };

  return (
    <>
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={isLoading}
        pagination={{ pageSize: 10 }}
        rowKey="key"
      />
      <Modal
        title="活動QRCode"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={400}>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          {currentEventUrl && (
            <>
              <canvas
                ref={canvasRef}
                style={{
                  display: 'inline-block',
                  padding: '16px',
                  background: 'white',
                  cursor: 'pointer',
                }}
                title="右鍵點擊可儲存圖片"
              />
              <Button
                type="primary"
                onClick={handleCopy}
                style={{ marginTop: '16px' }}>
                複製網址
              </Button>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

const EditModal = ({ isOpen, onClose, record, reFetchDataFunc, tagList }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (isOpen && record) {
      form.setFieldsValue({
        name: record.name,
        url: record.url,
        tags: record.tags,
        remark: record.remark,
      });
    } else if (isOpen) {
      form.resetFields();
    }
  }, [record, form, isOpen]);

  const handleSubmit = async (values) => {
    try {
      const data = {
        name: values.name,
        url: values.url,
        tags: values.tags,
        remark: values.remark,
      };
      if (record) {
        // Update
        data.id = record.id;
        return CampaignActions.updateCampaign(data);
      } else {
        // Create
        return CampaignActions.createCampaign(data);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      throw new Error('活動建立失敗');
    }
  };

  return (
    <Modal
      title={record ? `編輯活動: ${record.name}` : '新增活動'}
      open={isOpen}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      footer={null}
      width="50%">
      {isOpen && (
        <Form
          form={form}
          name="editCampaignForm"
          onFinish={(values) => {
            try {
              window.antdNotify(
                '活動入口碼',
                `正在${record ? '更新' : '建立'}活動...`,
                'info'
              );
              handleSubmit(values).then((result) => {
                if (result.success) {
                  window.antdNotify(
                    '活動入口碼',
                    `活動${record ? '更新' : '建立'}成功`,
                    'success'
                  );
                  reFetchDataFunc();
                  onClose();
                } else {
                  window.antdNotify(
                    '活動入口碼',
                    `活動${record ? '更新' : '建立'}失敗`,
                    'fail'
                  );
                }
              });
            } catch (error) {
              window.antdNotify(
                '活動入口碼',
                `活動${record ? '更新' : '建立'}失敗`,
                'fail'
              );
            }
          }}>
          <Form.Item
            label="活動名稱"
            name="name"
            rules={[{ required: true, message: '請輸入活動名稱' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="活動網址"
            name="url"
            rules={[{ required: true, message: '請輸入活動網址' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="Tag ID"
            name="tags"
            rules={[{ required: true, message: '請輸入Tag ID' }]}>
            <Select
              mode="multiple"
              allowClear
              placeholder="選擇欲使用的Tag ID"
              initialvalues={record ? record.tags : []}
              options={tagList}
            />
          </Form.Item>
          <Form.Item label="備註" name="remark">
            <Input.TextArea rows={4} />
          </Form.Item>
          <div style={{ textAlign: 'right', marginTop: '24px' }}>
            <Button onClick={onClose} style={{ marginRight: '8px' }}>
              關閉
            </Button>
            <Button type="primary" htmlType="submit" form="editCampaignForm">
              提交
            </Button>
          </div>
        </Form>
      )}
    </Modal>
  );
};

const CampaignMaintenancePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaignData, setCampaignData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shadowCampaign, setShadowCampaign] = useState(null);
  const [availableTags, setAvailableTags] = useState({
    fetching: 0,
    exception: false,
    data: [],
  });

  const inputSearchParams = { ...FilterSchema };
  Object.keys(inputSearchParams).forEach((key) => {
    if (searchParams.has(key)) {
      inputSearchParams[key] = searchParams.get(key);
    }
    if (searchParams.get('tag') === 'undefined') {
      inputSearchParams.tag = '';
    }
  });

  const fetchData = async () => {
    if (isFetching || availableTags.data.length === 0) return;
    setIsFetching(true);

    try {
      const data = await CampaignActions.getCampaignData(inputSearchParams);
      if (data.success) {
        setCampaignData(
          data.data.map((data) => ({ ...data, key: data.id })) || []
        );
      }
    } catch (error) {
      window.antdNotify('活動入口碼', '獲取資料失敗', 'fail');
    } finally {
      setIsFetching(false);
    }
  };
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, availableTags.data]);

  useEffect(() => {
    const fetchTags = async () => {
      const startFetching = setInterval(() => {
        setAvailableTags((prev) => ({
          ...prev,
          fetching: Math.min(prev.fetching + 0.4, 99.9),
        }));
      }, 100);

      try {
        const result = await getHotaiTags();
        clearInterval(startFetching);
        if (result.success) {
          setAvailableTags({
            data: result.data.map((tag) => ({
              label: tag.name,
              value: tag.val,
            })),
            fetching: 100,
          });
        } else {
          setAvailableTags((prev) => ({
            ...prev,
            exception: true,
          }));
          window.antdNotify('活動入口碼', '獲取TAG 清單失敗', 'fail');
        }
      } catch (error) {
        console.error('Error fetching tags:', error);
        setAvailableTags((prev) => ({
          ...prev,
          exception: true,
        }));
        if (startFetching) clearInterval(startFetching);
        window.antdNotify('活動入口碼', '獲取TAG 清單時發生錯誤', 'fail');
      }
    };

    fetchTags();
  }, []);

  return (
    <>
      <PageHeader functionName="lexus_campaign_reference" />
      <PageBody
        section={[
          {
            hasTitle: false,
            title: 'Search',
            children: (
              <SearchBar
                initialValues={inputSearchParams}
                onSearch={setSearchParams}
                onCreate={() => setIsModalOpen(true)}
                tagList={availableTags.data}
              />
            ),
          },
          {
            hasTitle: false,
            title: 'Table',
            children: (
              <>
                {availableTags.fetching < 100 && (
                  <Progress
                    percent={availableTags.fetching}
                    showInfo={false}
                    status={availableTags.exception ? 'exception' : 'active'}
                  />
                )}
                <DataTable
                  dataSource={campaignData}
                  isLoading={isFetching}
                  modalToggle={setIsModalOpen}
                  setShadow={setShadowCampaign}
                  reFetchDataFunc={fetchData}
                  tagList={availableTags.data}
                />
              </>
            ),
          },
          {
            hasTitle: false,
            title: 'Edit Modal',
            children: (
              <EditModal
                isOpen={isModalOpen}
                onClose={() => {
                  setIsModalOpen(false);
                  setShadowCampaign(null);
                }}
                record={shadowCampaign}
                reFetchDataFunc={fetchData}
                tagList={availableTags.data}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default CampaignMaintenancePage;
